import { Component, ElementRef, OnDestroy, OnInit } from '@angular/core';
import { FormControl, UntypedFormBuilder, FormGroup, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { CustomTranslateService } from '@services/common/custom-translate.service';
import { first } from 'rxjs/operators';
import { Subject } from 'rxjs';

import { AuthenticationService } from 'app/auth/service';
import { ToastService } from '@services/common/toast.service';
import { revealFormErrors } from 'app/main/helpers/form-validation';

interface ForgotPasswordFormGroup {
    email: FormControl<string>;
}

@Component({
    selector: 'app-forgot-password',
    templateUrl: './forgot-password.component.html',
    styleUrls: ['./forgot-password.component.scss'],
    standalone: false,
})
export class ForgotPasswordComponent implements OnInit, OnDestroy {
    // Public
    public forgotPasswordForm!: FormGroup<ForgotPasswordFormGroup>;
    public loading: boolean = false;
    public submitted: boolean = false;

    // Private
    private unsubscribeAll: Subject<void> = new Subject<void>();

    /**
     * Constructor
     *
     * @param {AuthenticationService} _authenticationService
     * @param {UntypedFormBuilder} _formBuilder
     * @param {MessageService} _messageService
     * @param {Router} _router
     * @param {CustomTranslateService} _translateService
     */
    constructor(
        private _authenticationService: AuthenticationService,
        private _formBuilder: UntypedFormBuilder,
        private toastService: ToastService,
        private _router: Router,
        private _translateService: CustomTranslateService,
        private readonly host: ElementRef<HTMLElement>,
    ) {
        // redirect to home if already logged in
        if (this._authenticationService.currentUserValue) {
            this._router.navigate(['/']);
        }

        this.unsubscribeAll = new Subject();
    }

    // convenience getter for easy access to form fields
    get f() {
        return this.forgotPasswordForm.controls;
    }

    /**
     * On Submit
     */
    onSubmit() {
        // stop here if form is invalid, showing what is missing
        if (!revealFormErrors(this.forgotPasswordForm, this.host.nativeElement)) {
            return;
        }

        // Forgot password
        this.loading = true;
        this._authenticationService
            .forgotPassword(this.f.email.value)
            .pipe(first())
            .subscribe({
                next: (data: string) => {
                    this.toastService.showInfo({
                        details: 'authentication.forgotPassword.message.instructionSent',
                        placeholderValue: {
                            value: data,
                        },
                    });
                    this.loading = false;
                },

                error: (error: Error) => {
                    this.toastService.showError({ details: error.message });
                    this.loading = false;
                },
            });
    }

    // Lifecycle Hooks
    // -----------------------------------------------------------------------------------------------------

    /**
     * On init
     */
    ngOnInit(): void {
        this.forgotPasswordForm = this._formBuilder.group({
            email: ['', [Validators.required, Validators.email]],
        }) as FormGroup<ForgotPasswordFormGroup>;
    }

    /**
     * On destroy
     */
    ngOnDestroy(): void {
        // Unsubscribe from all subscriptions
        this.unsubscribeAll.next();
        this.unsubscribeAll.complete();
    }
}
